// src/components/scenes/SceneCard.js
import React, { useState, useContext } from 'react';
import { FaSun, FaMoon, FaHome, FaFilm, FaBed, FaWalking, FaGlassCheers, FaTheaterMasks, FaChevronUp, FaChevronDown, FaEdit, FaTrash, FaPlay } from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import { toast } from 'react-toastify';
import './SceneCard.css';

const SceneCard = ({ scene, onEdit }) => {
  const { activateScene, deleteScene } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  // Handle scene activation
  const handleActivate = async () => {
    try {
      setLoading(true);
      await activateScene(scene.id);
      toast.success(`Scene "${scene.name}" activated successfully`);
    } catch (error) {
      console.log('Error activating scene:', error);
      toast.error(`Failed to activate scene "${scene.name}"`);
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle expanded state
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Handle scene delete
  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    try {
      setLoading(true);
      await deleteScene(scene.id);
      toast.success(`Scene "${scene.name}" deleted successfully`);
    } catch (error) {
      console.error('Error deleting scene:', error);
      toast.error(`Failed to delete scene "${scene.name}"`);
    } finally {
      setLoading(false);
      setDeleteConfirm(false);
    }
  };

  // Cancel delete confirmation
  const cancelDelete = () => {
    setDeleteConfirm(false);
  };
  
  // Render scene icon based on type
  const renderSceneIcon = () => {
    switch (scene.type) {
      case 'morning':
        return <FaSun />;
      case 'evening':
        return <FaMoon />;
      case 'welcome':
        return <FaHome />;
      case 'movie':
        return <FaFilm />;
      case 'sleep':
        return <FaBed />;
      case 'away':
        return <FaWalking />;
      case 'party':
        return <FaGlassCheers />;
      default:
        return <FaTheaterMasks />;
    }
  };
  
  return (
    <div className={`scene-card ${expanded ? 'expanded' : ''}`}>
      <div className="scene-main" onClick={toggleExpanded}>
        <div className="scene-icon">
          {renderSceneIcon()}
        </div>
        
        <div className="scene-info">
          <h3 className="scene-name">{scene.name}</h3>
          <p className="scene-description">{scene.description}</p>
        </div>
        
        <div className="scene-toggle">
          {expanded ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </div>
      
      {expanded && (
        <>
          <div className="scene-details">
            <div className="scene-actions-list">
              <h4>Actions:</h4>
              {scene.actions && scene.actions.length > 0 ? (
                <ul>
                  {scene.actions.map((action, index) => (
                    <li key={index} className={`action-item action-${action.type}`}>
                      {getActionDescription(action)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-actions">No actions defined for this scene</p>
              )}
            </div>
            
            <div className="scene-meta">
              {scene.category && (
                <div className="scene-category">
                  <span>Category:</span> {scene.category}
                </div>
              )}
              
              {scene.lastActivated && (
                <div className="scene-last-activated">
                  <span>Last activated:</span> {new Date(scene.lastActivated).toLocaleString()}
                </div>
              )}
            </div>
          </div>
          
          <div className="scene-controls">
            <button 
              className="edit-btn"
              onClick={() => onEdit(scene)}
              disabled={loading}
            >
              <FaEdit />
              <span>Edit</span>
            </button>
            
            {deleteConfirm ? (
              <div className="delete-confirm">
                <span>Are you sure?</span>
                <button 
                  className="confirm-yes" 
                  onClick={handleDelete}
                  disabled={loading}
                >
                  Yes
                </button>
                <button 
                  className="confirm-no"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  No
                </button>
              </div>
            ) : (
              <button 
                className="delete-btn"
                onClick={handleDelete}
                disabled={loading}
              >
                <FaTrash />
                <span>Delete</span>
              </button>
            )}
          </div>
        </>
      )}
      
      <div className="scene-controls scene-main-controls">
        <button 
          className="activate-btn"
          onClick={handleActivate}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="spinner-sm"></div>
              <span>Activating...</span>
            </>
          ) : (
            <>
              <FaPlay />
              <span>Activate</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Helper function to get human-readable action description
const getActionDescription = (action) => {
  switch (action.type) {
    case 'sound':
      return `Play sound: ${action.sound}`;
    case 'device':
      if (action.payload) {
        return `Control ${action.device}: ${JSON.stringify(action.payload)}`;
      }
      return `Control ${action.device}: ${action.command} ${action.value || ''}`;
    case 'delay':
      return `Wait for ${action.duration} ms`;
    case 'notification':
      return `Notification: ${action.message}`;
    default:
      return `${action.type}: ${JSON.stringify(action)}`;
  }
};

export default SceneCard;